import type { CSSProperties, ReactNode, UIEvent } from 'react';
import { useRef } from 'react';
import { noop } from 'lodash';
import { AvatarForWorkspace } from '@frameio/shared/components/AvatarForWorkspace';
import {
  Box,
  Button,
  Select,
  Text,
  TextFieldHelp,
  theme,
} from '@frameio/vapor';
import { IconChevronDown, IconSelect } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/WorkspacePicker';

const MAX_WORKSPACE_PICKER_HEIGHT = 280;
TEXT.DEFINE({
  /** Label for the workspace picker field */
  title: 'Workspace',
  /** Placeholder text for selecting a workspace */
  selectWorkspace: 'Select a Workspace',
  /** Help text explaining that workspace cannot be changed */
  helpText: "A Project's Workspace can't be changed.",
});
type WorkspacePickerProps = {
  selectedWorkspace?: {
    name?: string | null;
    images?: {
      image128?: string | null;
    } | null;
  };
  isDisabled?: boolean;
  workspaceOptions?: Array<{
    iconBefore: ReactNode;
    label: string;
    value: string;
  }>;
  setSelectedWorkspaceId?: (selectedWorkspaceId: string) => void;
  style?: CSSProperties;
  width?: number;
  onScroll?: (event: UIEvent<HTMLDivElement>) => void;
};
export const WorkspacePicker = ({
  selectedWorkspace,
  workspaceOptions = [],
  setSelectedWorkspaceId = noop,
  style = {},
  width = 400,
  isDisabled,
  onScroll = noop,
}: WorkspacePickerProps) => {
  const workspaceSelectRef = useRef<HTMLSelectElement>(null);
  return (
    <Box style={{ ...style, width: '100%' }}>
      <Text
        color={isDisabled ? 'disabled' : 'secondary'}
        style={{ textAlign: 'left', marginBottom: theme.spaces[4] }}
        truncate={true}
        variant="secondary"
      >
        {TEXT.title}
      </Text>
      <Select
        content={workspaceOptions}
        isDisabled={isDisabled}
        maxHeight={`${MAX_WORKSPACE_PICKER_HEIGHT}px`}
        maxWidth="100%"
        name={TEXT.selectWorkspace}
        onChange={(picked) => {
          setSelectedWorkspaceId(picked);
          workspaceSelectRef?.current?.focus();
        }}
        onScroll={onScroll}
        placement="bottom-start"
        width={`${width}px`}
      >
        <Button
          ref={workspaceSelectRef}
          iconAfter={
            !isDisabled ? (
              <IconChevronDown
                color={
                  isDisabled
                    ? theme.colors.text.interactive.disabled
                    : theme.colors.icon.secondary
                }
                size={16}
              />
            ) : undefined
          }
          iconBefore={
            selectedWorkspace ? (
              <AvatarForWorkspace
                alt={selectedWorkspace.name || ''}
                isDisabled={isDisabled}
                size={20}
                src={selectedWorkspace.images?.image128 || undefined}
              />
            ) : (
              <IconSelect color={theme.colors.icon.primary} />
            )
          }
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            gap: theme.spaces[2],
            pointerEvents: isDisabled ? 'initial' : 'auto',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            background: isDisabled
              ? theme.colors.components.button.secondary.interactive.background
                  .disabled
              : theme.colors.components.input.background,
          }}
        >
          <Text
            color={isDisabled ? 'disabled' : 'body'}
            style={{ textAlign: 'left', flexGrow: 1 }}
            truncate={true}
            variant="body"
          >
            {selectedWorkspace ? selectedWorkspace.name : TEXT.selectWorkspace}
          </Text>
        </Button>
      </Select>
      {isDisabled && (
        <TextFieldHelp color="tertiary" paddingLeft={1} paddingTop={8}>
          {TEXT.helpText}
        </TextFieldHelp>
      )}
    </Box>
  );
};
